import { useState, useEffect } from "react";
import axios from "axios";
import { useToast } from "@/components/ui/use-toast";

// Define the backend API URL
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000"; // Fallback to localhost if env var is not set

type EmbeddingStatus = "not_started" | "processing" | "completed" | "error";

interface UseEmbeddingStatusProps {
  resumeId: number | null;
  initialStatus?: EmbeddingStatus;
  pollingInterval?: number; // in milliseconds
}

interface UseEmbeddingStatusReturn {
  status: EmbeddingStatus;
  progress: number;
  error: string | null;
  checkStatus: () => Promise<void>;
}

export function useEmbeddingStatus({
  resumeId,
  initialStatus = "not_started",
  pollingInterval = 3000,
}: UseEmbeddingStatusProps): UseEmbeddingStatusReturn {
  const [status, setStatus] = useState<EmbeddingStatus>(initialStatus);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Function to check embedding status
  const checkStatus = async () => {
    if (!resumeId) return;

    try {
      const response = await axios.get(
        `${API_URL}/api/embedding-status/${resumeId}`
      );

      if (response.data.embeddingsComplete) {
        setStatus("completed");
        setProgress(100);

        // Show success toast only once when transitioning to completed
        if (status === "processing") {
          toast({
            title: "Embeddings Complete!",
            description:
              "Your resume is now fully processed and ready for chatting.",
          });
        }
      } else if (status === "processing") {
        // Gradually increase progress to show ongoing work
        setProgress((prev) => Math.min(prev + 5, 90));
      }
    } catch (err) {
      console.error("Error checking embedding status:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Unknown error checking embedding status"
      );

      // Don't change status to error - keep trying in case it's a temporary issue
      toast({
        variant: "destructive",
        title: "Status check failed",
        description: "Failed to check embedding status. Retrying...",
      });
    }
  };

  // Setup polling effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (resumeId !== null && status === "processing") {
      // Initial progress to indicate processing has begun
      if (progress === 0) setProgress(20);

      // Start polling
      checkStatus();
      intervalId = setInterval(checkStatus, pollingInterval);
    }

    // Cleanup on unmount or when status changes
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [resumeId, status, pollingInterval]);

  return {
    status,
    progress,
    error,
    checkStatus,
  };
}
